import type { MetadataEntity, MetadataType } from '../../domain/entities/MetadataEntity.js';
import type { ProjectMetadataAdoptionEntity } from '../../domain/entities/ProjectMetadataAdoptionEntity.js';
import type {
  IMetadataRepository, MetadataCreateInput, MetadataListOptions, MetadataSearchOptions, MetadataUpdateInput,
  ProjectMetadataAdoptionsListOptions,
} from '../../domain/repositories/IMetadataRepository.js';
import { MetadataRemoteDataSource } from '../datasources/MetadataRemoteDataSource.js';
import type { MetadataDto } from '../dtos/MetadataDto.js';

function mapDto(dto: MetadataDto): MetadataEntity {
  return {
    id: dto.id, orgId: dto.org_id, projectId: dto.project_id ?? null,
    type: dto.type as MetadataType, name: dto.name,
    description: dto.description ?? null, createdAt: dto.created_at, updatedAt: dto.updated_at,
  };
}

export class MetadataRepository implements IMetadataRepository {
  constructor(private readonly ds: MetadataRemoteDataSource) {}

  async getById(id: string): Promise<MetadataEntity | null> {
    const dto = await this.ds.getById(id);
    return dto ? mapDto(dto) : null;
  }

  async list(options: MetadataListOptions): Promise<MetadataEntity[]> {
    return (await this.ds.list(options)).map(mapDto);
  }

  async searchMetadatas(options: MetadataSearchOptions): Promise<MetadataEntity[]> {
    return (await this.ds.searchMetadatas(options)).map(mapDto);
  }

  async create(input: MetadataCreateInput): Promise<MetadataEntity> {
    const dto = await this.ds.create({
      org_id: input.orgId, project_id: input.projectId ?? null,
      type: input.type, name: input.name, description: input.description ?? null,
    });
    if (!dto) throw new Error('Metadata creation failed');
    return mapDto(dto);
  }

  async update(id: string, input: MetadataUpdateInput): Promise<MetadataEntity | null> {
    const payload: Partial<MetadataDto> = {};
    if (input.name !== undefined) payload.name = input.name;
    if (input.description !== undefined) payload.description = input.description;
    if (input.type !== undefined) payload.type = input.type;
    const dto = await this.ds.update(id, payload);
    return dto ? mapDto(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.ds.delete(id);
  }

  async listProjectAdoptions(options: ProjectMetadataAdoptionsListOptions): Promise<ProjectMetadataAdoptionEntity[]> {
    const [metas, adoptions] = await Promise.all([
      this.ds.list({ orgId: options.orgId, type: options.type }),
      this.ds.listProjectAdoptions(options.projectId),
    ]);

    const adoptionByMeta = new Map(adoptions.map((a) => [a.metadata_id, a]));

    const visible = metas.filter((m) => {
      if (m.project_id === null || m.project_id === undefined) return true;
      return m.project_id === options.projectId;
    });

    return visible.map((m) => {
      const adoption = adoptionByMeta.get(m.id);
      return {
        metadata: mapDto(m),
        adopted: !!adoption,
        displayName: adoption?.display_name ?? null,
      };
    });
  }

  async adopt(projectId: string, metadataId: string): Promise<void> {
    await this.ds.adopt(projectId, metadataId);
  }

  async unadopt(projectId: string, metadataId: string): Promise<void> {
    await this.ds.unadopt(projectId, metadataId);
  }

  async updateAdoption(projectId: string, metadataId: string, displayName: string | null): Promise<void> {
    await this.ds.updateAdoption(projectId, metadataId, displayName);
  }
}
